import type { ReactNode } from "react";
import { LOADING_CHECKOUT } from "@libs/checkout-core";

/**
 * The one button at the bottom of the tickets sheet: Continue on the
 * tickets, "Loading checkout" with its spinner while the tickets are held
 * and the checkout gets ready, then the checkout's Pay and the payment's
 * status — the same button throughout, its words morphing, never swapped
 * for another. The sheet draws it; each screen says what it should be.
 */
/** The status's mark beside the words: the engine's spinner and green
 *  tick, and a vendor's prepared checkout's `ready` and `stopped`. */
export type SheetButtonIcon = "spinner" | "tick" | "ready" | "stopped";

export type SheetButtonState = {
  words: string;
  icon: SheetButtonIcon | null;
  /** From the fold on: the fill has gone, the words stand alone. */
  plain: boolean;
  done: boolean;
  disabled: boolean;
  onPress: () => void;
};

/** What the checkout tells the sheet: its button (none on the screens
 *  that end it, which have their own), and whether to keep its height. */
export type CheckoutChrome = {
  button: SheetButtonState | null;
  hold: boolean;
  /** Drawn under the button (a vendor's outcome and its actions); kept the
   *  same element until it changes, so handing it on never loops. */
  after?: ReactNode;
  /** The button stays at the foot while the keyboard is up instead of
   *  riding it: what is typed into is a web page that keeps its own field
   *  above the keyboard (a bank's check), and the button would cover it. */
  stayPut?: boolean;
};

const nothing = () => undefined;

export const LOADING_BUTTON: SheetButtonState = {
  words: LOADING_CHECKOUT,
  icon: "spinner",
  plain: false,
  done: false,
  disabled: true,
  onPress: nothing,
};

/** The room under the sheet's button (TicketsSheetFrame's): the safe
 *  area's, or 16 at least. */
export const sheetFoot = (bottomInset: number) => Math.max(bottomInset, 16);

/** How far above the keyboard a sticky sheet button rides. */
export const KEYBOARD_GAP = 12;
